import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  useColorScheme,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { usePoll } from '../hooks/usePoll';

interface PollMessageProps {
  pollId: string;
  currentUserId: string;
  isOwnMessage?: boolean;
}

/**
 * Component to display a poll in a chat message
 */
export const PollMessage: React.FC<PollMessageProps> = ({
  pollId,
  currentUserId,
  isOwnMessage = false,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const { poll, loading, voting, vote, closePoll } = usePoll(pollId, currentUserId);

  const colors = {
    background: isDark ? '#1F2937' : '#FFFFFF',
    surface: isDark ? '#374151' : '#F3F4F6',
    text: isDark ? '#F9FAFB' : '#111827',
    textSecondary: isDark ? '#9CA3AF' : '#6B7280',
    primary: '#7C3AED',
    primaryLight: isDark ? '#7C3AED30' : '#7C3AED20',
    border: isDark ? '#4B5563' : '#E5E7EB',
    success: '#10B981',
    voted: '#7C3AED',
    ownBubble: '#7C3AED',
  };

  // Calculate percentages
  const optionsWithPercentage = useMemo(() => {
    if (!poll?.options) return [];
    
    return poll.options.map((option) => ({
      ...option,
      percentage: poll.total_votes > 0 
        ? Math.round((option.vote_count / poll.total_votes) * 100) 
        : 0,
    }));
  }, [poll]);

  const handleVote = async (optionId: string) => {
    if (!poll?.is_closed) {
      await vote(optionId);
    }
  };

  const handleClosePoll = async () => {
    await closePoll();
  };

  /**
   * Format the deadline date for display
   */
  const formatDeadline = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) {
      return 'verlopen';
    }

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `over ${diffMins} min`;
    }

    if (diffHours < 24) {
      return `over ${diffHours} uur`;
    }

    if (diffDays < 7) {
      return date.toLocaleDateString('nl-NL', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <View
        className="rounded-2xl p-4 min-w-[250px] max-w-[300px]"
        style={{ backgroundColor: isOwnMessage ? colors.ownBubble : colors.background }}
      >
        <ActivityIndicator size="small" color={isOwnMessage ? '#FFFFFF' : colors.primary} />
      </View>
    );
  }

  if (!poll) {
    return (
      <View
        className="rounded-2xl p-4 min-w-[250px] max-w-[300px]"
        style={{ backgroundColor: isOwnMessage ? colors.ownBubble : colors.background }}
      >
        <Text style={{ color: isOwnMessage ? '#FFFFFF' : colors.textSecondary }}>
          Poll niet gevonden
        </Text>
      </View>
    );
  }

  const canClose = poll.created_by === currentUserId && !poll.is_closed;
  const showResults = poll.has_voted || poll.is_closed;

  return (
    <View
      className="rounded-2xl p-4 min-w-[250px] max-w-[300px]"
      style={{
        backgroundColor: isOwnMessage ? colors.ownBubble : colors.background,
        borderWidth: isOwnMessage ? 0 : 1,
        borderColor: colors.border,
      }}
    >
      {/* Poll Header */}
      <View className="flex-row items-start mb-3">
        <View
          className="w-8 h-8 rounded-full items-center justify-center mr-2"
          style={{ backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.2)' : colors.primaryLight }}
        >
          <Ionicons
            name="bar-chart-outline"
            size={18}
            color={isOwnMessage ? '#FFFFFF' : colors.primary}
          />
        </View>
        <View className="flex-1">
          <Text
            style={{ color: isOwnMessage ? '#FFFFFF' : colors.text }}
            className="text-base font-semibold leading-5"
          >
            {poll.question}
          </Text>
          {poll.is_anonymous && (
            <View className="flex-row items-center mt-1">
              <Ionicons
                name="eye-off-outline"
                size={12}
                color={isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary}
              />
              <Text
                style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary }}
                className="text-xs ml-1"
              >
                Anonieme poll
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Poll Options */}
      <View className="space-y-2">
        {optionsWithPercentage.map((option) => (
          <TouchableOpacity
            key={option.id}
            onPress={() => handleVote(option.id)}
            disabled={poll.is_closed || voting || (poll.has_voted && !poll.allows_multiple_answers && !option.voted_by_current_user)}
            activeOpacity={0.7}
            className="relative overflow-hidden rounded-xl"
            style={{
              backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : colors.surface,
              borderWidth: option.voted_by_current_user ? 2 : 1,
              borderColor: option.voted_by_current_user
                ? isOwnMessage ? '#FFFFFF' : colors.voted
                : isOwnMessage ? 'rgba(255,255,255,0.3)' : colors.border,
            }}
          >
            {/* Progress Bar Background */}
            {showResults && (
              <View
                className="absolute left-0 top-0 bottom-0 rounded-xl"
                style={{
                  width: `${option.percentage}%`,
                  backgroundColor: option.voted_by_current_user
                    ? isOwnMessage ? 'rgba(255,255,255,0.3)' : colors.primaryLight
                    : isOwnMessage ? 'rgba(255,255,255,0.1)' : colors.primaryLight + '50',
                }}
              />
            )}

            <View className="flex-row items-center justify-between px-3 py-2.5 relative z-10">
              <View className="flex-row items-center flex-1">
                {option.voted_by_current_user ? (
                  <Ionicons
                    name="checkmark-circle"
                    size={20}
                    color={isOwnMessage ? '#FFFFFF' : colors.voted}
                  />
                ) : (
                  <View
                    className="w-5 h-5 rounded-full border-2"
                    style={{
                      borderColor: isOwnMessage ? 'rgba(255,255,255,0.5)' : colors.border,
                    }}
                  />
                )}
                <Text
                  style={{
                    color: isOwnMessage ? '#FFFFFF' : colors.text,
                    fontWeight: option.voted_by_current_user ? '600' : '400',
                  }}
                  className="text-sm ml-2 flex-1"
                  numberOfLines={2}
                >
                  {option.option_text}
                </Text>
              </View>

              {showResults && (
                <View className="ml-2 flex-row items-center">
                  <Text
                    style={{ color: isOwnMessage ? 'rgba(255,255,255,0.9)' : colors.textSecondary }}
                    className="text-xs font-medium"
                  >
                    {option.vote_count} ({option.percentage}%)
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Poll Footer */}
      <View className="mt-3">
        <View className="flex-row items-center justify-between">
          <Text
            style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary }}
            className="text-xs"
          >
            {poll.total_votes} {poll.total_votes === 1 ? 'stem' : 'stemmen'}
            {poll.allows_multiple_answers && ' • Meerdere opties'}
          </Text>

          {poll.is_closed && (
            <View className="flex-row items-center">
              <Ionicons
                name="lock-closed"
                size={12}
                color={isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary}
              />
              <Text
                style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary }}
                className="text-xs ml-1"
              >
                Gesloten
              </Text>
            </View>
          )}

          {canClose && (
            <TouchableOpacity
              onPress={handleClosePoll}
              className="flex-row items-center"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name="lock-closed-outline"
                size={14}
                color={isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary}
              />
              <Text
                style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary }}
                className="text-xs ml-1"
              >
                Sluiten
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Deadline indicator */}
        {poll.closes_at && !poll.is_closed && (
          <View className="flex-row items-center mt-2">
            <Ionicons
              name="time-outline"
              size={12}
              color={isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary}
            />
            <Text
              style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : colors.textSecondary }}
              className="text-xs ml-1"
            >
              Sluit {formatDeadline(poll.closes_at)}
            </Text>
          </View>
        )}
      </View>

      {/* Voting indicator */}
      {voting && (
        <View className="absolute inset-0 items-center justify-center bg-black/20 rounded-2xl">
          <ActivityIndicator size="small" color="#FFFFFF" />
        </View>
      )}
    </View>
  );
};

